import { QueryStringParams, PaginatedResponse } from '@xtnd/web/shared/types';
import queryString from 'query-string';
import { omitBy, isUndefined, isPlainObject, isEmpty, get } from 'lodash';

import { api } from '@xtnd/web/shared/api';

import {
  Appeal,
  AppealHistory,
  AppealGroup,
  UpdateAppealStatusPayload,
  EditAppealSubmittedPayload,
  EditAppealPayload,
  AppealItem,
} from '../types';
import { ApiResponse, GroupByOption } from '@repo/types';

/**
 * Service class for managing appeals reporting with mock data capabilities.
 * Provides CRUD operations and filtering/sorting/pagination functionality for appeals data.
 *
 * @class ReportingAppealsService
 * @example
 * ```typescript
 * const service = new ReportingAppealsService();
 * const appeals = await service.getAppeals({ page: 1, limit: 20, status: 'APPEAL_SUBMITTED' });
 * ```
 */
class ReportingAppealsService {
  private readonly baseResource = '/ar-collections';
  private readonly baseService = '/appeals';

  /**
   * Normalizes API appeal payloads into the frontend `Appeal` shape.
   *
   * @param data - Raw appeal response body from the backend.
   * @returns Appeal model with consistent fields and defaults.
   */
  private readonly _mapper = (data: any): Appeal => {
    const amountPaid = get(data, 'paid', 0) + get(data, 'creditsIssued', 0);
    return {
      tenantId: get(data, 'tenantId'),
      invoiceId: get(data, 'invoiceId'),
      clientId: get(data, 'clientId'),
      invoiceNumber: get(data, 'invoiceNumber'),
      invoiceStatus: get(data, 'state'),
      invoiceDate: get(data, 'invoiceDate'),
      clientName: get(data, 'clientName'),
      matterDisplayNumber: get(data, 'matterDisplayNumber'),
      matterDescription: get(data, 'matterDescription'),
      totalAmount: get(data, 'total', 0),
      amountPaid,
      amountDue: get(data, 'outstandingBalance', 0),
      status: get(data, 'appealStatus'),
      deadline: get(data, 'appeal.deadline'),
      attyDeadline: get(data, 'appeal.attyDeadline'),
      splitInvoice: get(data, 'splitInvoice'),
      collectedAmount: get(data, 'appeal.collectedAmount', 0),
      remainingAmount: get(data, 'appeal.remainingAmount', 0),
      excludeFromAutoWriteOff: get(data, 'excludeFromAutoWriteOff', false),
      creditsIssued: get(data, 'creditsIssued', 0),
      paid: get(data, 'paid', 0),
      writeOffAmount: get(data, 'writeOffAmount', 0),
      actualBalance: get(data, 'actualBalance', 0),
      assignedTo: get(data, 'assignedTo'),
      responsibleAttorneyName: get(data, 'responsibleAttorneyName'),
      appealedFees: get(data, 'appeal.appealedFees'),
      appealedExpenses: get(data, 'appeal.appealedExpenses'),
      approvedWriteOffAmount: get(data, 'approvedWriteOffAmount', 0),
      approvedPromptPayment: get(data, 'appeal.approvedPromptPayment', 0),
      finalAppeal: get(data, 'appeal.finalAppeal', false),
      updatedAt: get(data, 'updatedAt'),
      appealType: get(data, 'appeal.appealType'),
    };
  };

  private readonly _groupsMapper = (data: any): AppealGroup => {
    const amountPaid = get(data, 'paid', 0) + get(data, 'creditsIssued', 0);

    return {
      groupId: get(data, 'groupId'),
      groupName: get(data, 'groupName'),
      totalAmount: get(data, 'total', 0),
      amountPaid,
      totalInvoices: get(data, 'totalInvoices', 0),
      maxDaysPastDue: get(data, 'maxDaysPastDue', 0),
      creditsIssued: get(data, 'creditsIssued', 0),
      amountDue: get(data, 'outstandingBalance', 0),
      paid: get(data, 'paid', 0),
    };
  };

  /**
   * Retrieves paginated appeals with filtering, sorting, and pagination options.
   *
   * @param params - Query string parameters including filters and pagination metadata.
   * @returns Paginated appeal list with metadata.
   */
  async getAppeals(params: QueryStringParams): Promise<PaginatedResponse<Appeal | AppealGroup>> {
    const paramsCleaned = omitBy(
      params,
      value => isUndefined(value) || (isPlainObject(value) && isEmpty(value))
    );

    const query = queryString.stringify(
      {
        ...paramsCleaned,
        filters: paramsCleaned.filters ? JSON.stringify(paramsCleaned.filters) : undefined,
      },
      { arrayFormat: 'comma' }
    );
    let endpoint = `${this.baseResource}${this.baseService}`;
    if (query) {
      endpoint = `${endpoint}?${query}`;
    }

    const response = await api.get<PaginatedResponse<Appeal>>(endpoint);
    const groupBy = get(paramsCleaned, 'filters.groupBy', GroupByOption.NONE);
    if (groupBy === GroupByOption.NONE) {
      return {
        data: response.data.map(this._mapper),
        pagination: response.pagination,
      };
    }

    return {
      data: response.data.map(data => this._groupsMapper(data)),
      pagination: response.pagination,
    };
  }
  /**
   * Updates an appeal status for a given invoice and returns the normalized appeal.
   *
   * @param payload - Identifier and fields required to update the appeal status.
   * @returns Updated appeal wrapped in a data object.
   */
  async updateAppealStatus(payload: UpdateAppealStatusPayload): Promise<{ data: Appeal }> {
    const endpoint = `${this.baseResource}/invoices/${payload.invoiceId}/appeal`;
    const response = await api.patch<Appeal>(endpoint, payload);

    const appealUpdated = this._mapper(response);
    return {
      data: appealUpdated,
    };
  }

  /**
   * Edits the appeal of an invoice in-place. Hits the dedicated edit endpoint with the
   * per-status payload and the optimistic-lock token (`expectedUpdatedAt`). The BE
   * dispatches on the invoice's stored `(status, appealType)` and validates against the
   * matching per-status DTO.
   *
   * @param invoiceId - Invoice ID to edit.
   * @param payload - Edit payload (narrow `EditAppealSubmittedPayload` or wide
   *                  `EditAppealConfigPayload`) including `expectedUpdatedAt`
   *                  read at modal open.
   * @returns Updated appeal wrapped in a data object.
   */
  async editAppeal(
    invoiceId: string,
    payload: EditAppealPayload
  ): Promise<{ data: Appeal }> {
    const endpoint = `${this.baseResource}/invoices/${invoiceId}/edit`;
    const response = await api.patch<Appeal>(endpoint, payload);

    const appealUpdated = this._mapper(response);
    return {
      data: appealUpdated,
    };
  }

  /**
   * Updates appeal deadlines for a given invoice without changing the appeal status.
   *
   * @param payload - Identifier and deadline fields to update.
   * @returns Updated appeal wrapped in a data object.
   */
  async updateAppealDeadlines(payload: {
    invoiceId: string;
    appealId: string;
    deadline?: string | null;
    attyDeadline?: string | null;
  }): Promise<{ data: Appeal }> {
    const endpoint = `${this.baseResource}/invoices/${payload.invoiceId}/appeal/${payload.appealId}/deadlines`;
    const response = await api.patch<Appeal>(endpoint, payload);

    const appealUpdated = this._mapper(response);
    return {
      data: appealUpdated,
    };
  }

  /**
   * Retrieves the appeal history for a specific invoice.
   *
   * @param invoiceId - The ID of the invoice to fetch appeal history for
   * @returns Promise resolving to an array of appeal history records
   */
  async getAppealHistory(invoiceId: string): Promise<AppealHistory[]> {
    const endpoint = `${this.baseResource}/invoices/${invoiceId}/appeal/history`;
    const response = await api.get<PaginatedResponse<AppealHistory>>(endpoint);
    const history = get(response, 'data', []);
    return history;
  }

  /**
   * Retrieves the appeals for a specific group (client or attorney).
   *
   * @param groupId - The ID of the group (client or attorney) to fetch appeals for
   * @returns Promise resolving to an array of appeals
   */
  async getAppealsByGroupId(
    groupId: string,
    query: string
  ): Promise<PaginatedResponse<Appeal | AppealGroup>> {
    const endpoint = `${this.baseResource}/appeals/group/${groupId}${query ? `?${query}` : ''}`;
    const response = await api.get<PaginatedResponse<Appeal>>(endpoint);
    return {
      data: response.data.map(this._mapper),
      pagination: response.pagination,
    };
  }

  /**
   * Retrieves the appeals for a specific invoice.
   *
   * @param invoiceId - The ID of the invoice to fetch appeals for
   * @returns Promise resolving to an array of appeals
   */
  async getAppealList(invoiceId: string): Promise<AppealItem[]> {
    const endpoint = `${this.baseResource}/invoices/${invoiceId}/appeals`;
    const response = await api.get<ApiResponse<AppealItem[]>>(endpoint);
    return response.data;
  }
}

/**
 * Singleton instance of ReportingAppealsService for application-wide use.
 * Provides a shared service instance with persistent mock data across the application.
 *
 * @constant
 * @type {ReportingAppealsService}
 *
 * @example
 * ```typescript
 * import { reportingAppealsApi } from './services/reporting-appeals';
 *
 * // Use the singleton instance
 * const appeals = await reportingAppealsApi.getAppeals({ page: 1 });
 * ```
 */
export const reportingAppealsApi = new ReportingAppealsService();
